import React, { useEffect, useMemo, useState } from 'react';
import { useForm, useFormState } from 'react-final-form';
import { Button } from 'react-bootstrap';
import { IEmailTemplate } from '@/typings';
import { useEditorProps } from '@/hooks/useEditorProps';
import { getDismissKey, isDismissed, rememberDismissed } from './dismissal';

/**
 * Notice that the open-time backfill filled in settings some blocks were missing.
 * There is nothing to decide — the backfill only writes values that were absent,
 * so it can never overwrite an edit — but Undo puts the template back exactly as
 * it was handed in, and the ✕ dismisses. Once the template is saved with the
 * filled-in values it stops appearing.
 */
export function ReconciliationNotice() {
  const { data } = useEditorProps();
  const form = useForm<IEmailTemplate>();
  const { values } = useFormState<IEmailTemplate>({
    subscription: { values: true },
  });

  // ✕ is remembered for this exact template + update list, so reopening an
  // unchanged, unsaved template doesn't show the same notice again (monday
  // 12868136019). New content or new updates show it again.
  const upgradedBlocks = values.reconciliation?.upgradedBlocks || [];
  const blocksSignature = upgradedBlocks.join(',');
  const dismissKey = useMemo(
    () => getDismissKey(data?.content, upgradedBlocks),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data?.content, blocksSignature],
  );
  const [dismissed, setDismissed] = useState(() => isDismissed(dismissKey));
  useEffect(() => {
    if (isDismissed(dismissKey)) setDismissed(true);
  }, [dismissKey]);

  // Saving accepts the backfill, so the notice's job is done — there is nothing
  // left to undo (the save persisted the filled-in values). The editor fires
  // this event only after a save actually succeeded. Without it the banner sat
  // there until the template was closed and reopened.
  useEffect(() => {
    const onSaved = () => setDismissed(true);
    window.addEventListener('email-template-saved', onSaved);
    return () => window.removeEventListener('email-template-saved', onSaved);
  }, []);

  if (dismissed || !upgradedBlocks.length) {
    return null;
  }

  const undoUpdates = () => {
    if (data?.content) {
      form.change('content', data.content);
    }

    form.change('reconciliation', {
      ...values.reconciliation,
      upgradedBlocks: [],
    });

    setDismissed(true);
  };

  return (
    <div
      style={{
        borderBottom: '1px solid #e5e6eb',
        backgroundColor: '#e8f4ff',
        padding: '10px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        flexWrap: 'wrap',
      }}
    >
      <div>
        <div style={{ color: '#4e5969', fontSize: 14 }}>
          Updates applied. You can undo them if needed.
        </div>
        <div style={{ color: '#86909c', fontSize: 13 }}>
          {upgradedBlocks.join(', ')}
        </div>
      </div>

      <div className='d-flex align-items-center gap-2'>
        <Button variant='outline-secondary' onClick={undoUpdates}>
          Undo updates
        </Button>
        <Button
          variant='link'
          aria-label='Dismiss'
          onClick={() => {
            rememberDismissed(dismissKey);
            setDismissed(true);
          }}
          style={{ color: '#4e5969', textDecoration: 'none', padding: '0 6px' }}
        >
          ✕
        </Button>
      </div>
    </div>
  );
}
